import { catalogCrossTabLog } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogCrossTabDebug'
import {
  clearCatalogOperationTimeoutNotice,
  readCatalogOperationTimeoutNotice,
  setCatalogOperationTimeoutNotice,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogOperationTimeoutNotice'
import {
  type CrossTabCatalogModeBusy,
  readCrossTabCatalogModeBusy,
  subscribeCrossTabCatalogModeBusy,
  writeCrossTabCatalogModeBusy,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogServiceCrossTab'
import {
  resolveConfirmedCatalogCalibKey,
  resolveConfirmedCatalogServiceKey,
  parseCatalogCalibItemKeyFromActionKey,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibLotService'
import type { ServiceCalibCatalogData } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibTypes'
import { getEffectiveServicePointCalibrationRows, isServiceCalibItemCalibrated, serviceCalibAwaitOutcomeMatches } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibUtils'
import { useEffect, useMemo, useState, useSyncExternalStore, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RobotActions } from 'store/actions/robot'
import { selectRobotTelemetry } from 'store/selectors/robot'
import { selectRobotActiveMode } from 'store/selectors/robot'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import {
  CALIB_FINISH_AWAIT_TIMEOUT_MS,
  CATALOG_OPERATION_TIMEOUT_MS,
  MODE_SWITCH_TIMEOUT_PADDING_MS,
  isCatalogCalibFinishAction,
  isLocalCatalogDispatchSettled,
  resolveLocalCatalogDispatch,
  shouldClearStaleCrossTabCatalogBusy,
} from 'utils/catalogOperationSettlement'
import { ROBOT_MODES } from 'utils/constants'
import { RootState } from 'utils/interfaces'

function useCrossTabCatalogModeBusy(robotId: number | undefined): CrossTabCatalogModeBusy | null {
  return useSyncExternalStore(
    onStoreChange => {
      if (!robotId) {
        return () => {}
      }
      return subscribeCrossTabCatalogModeBusy(robotId, onStoreChange)
    },
    () => (robotId ? readCrossTabCatalogModeBusy(robotId) : null),
    () => null
  )
}

export function useServiceModeSwitchWaiting(catalog: ServiceCalibCatalogData | null = null) {
  const dispatch = useDispatch()
  const telemetry = useSelector(selectRobotTelemetry)
  const activeMode = useSelector(selectRobotActiveMode)
  const optimistic = useSelector((state: RootState) => state.robot.serviceModeSwitchOptimistic)
  const robotInfo = useSelector((state: RootState) => state.robot.robotInfo)
  const robotId = robotInfo?.id
  const serverPending = robotInfo?.modeSwitchPending
  const activeModeMetadata = robotInfo?.activeModeMetadata
  const crossTabBusy = useCrossTabCatalogModeBusy(robotId)

  const localDispatch = resolveLocalCatalogDispatch(optimistic, crossTabBusy)

  const calibrationRows = getEffectiveServicePointCalibrationRows(telemetry, {
    activeMode,
    activeModeMetadata,
  })

  const confirmedKeys = useMemo(
    () => ({
      calibKey: resolveConfirmedCatalogCalibKey(catalog, activeMode, activeModeMetadata),
      serviceKey: resolveConfirmedCatalogServiceKey(catalog, activeMode, activeModeMetadata),
    }),
    [activeMode, activeModeMetadata, catalog]
  )

  const operationSettled = isLocalCatalogDispatchSettled(
    localDispatch,
    calibrationRows,
    activeMode,
    serverPending,
    confirmedKeys
  )

  const calibFinishOnboardReadyKey = useMemo(() => {
    if (!localDispatch || !isCatalogCalibFinishAction(localDispatch.actionKey) || !localDispatch.awaitCalib) {
      return null
    }
    if (!serviceCalibAwaitOutcomeMatches(localDispatch.awaitCalib, calibrationRows)) {
      return null
    }
    return parseCatalogCalibItemKeyFromActionKey(localDispatch.actionKey)
  }, [calibrationRows, localDispatch])

  const [recentCalibFinishSuccessKey, setRecentCalibFinishSuccessKey] = useState<string | null>(null)

  useEffect(() => {
    if (!calibFinishOnboardReadyKey) {
      return
    }
    setRecentCalibFinishSuccessKey(calibFinishOnboardReadyKey)
  }, [calibFinishOnboardReadyKey])

  useEffect(() => {
    if (!recentCalibFinishSuccessKey || !catalog) {
      return
    }
    const item = [...catalog.stationGroups.flatMap(g => g.lots), ...catalog.maintenancePoints].find(
      row => row.key === recentCalibFinishSuccessKey
    )
    if (!item) {
      return
    }
    const rows = getEffectiveServicePointCalibrationRows(telemetry, {
      activeMode,
      activeModeMetadata,
    })
    if (isServiceCalibItemCalibrated(item, rows)) {
      setRecentCalibFinishSuccessKey(null)
    }
  }, [activeMode, activeModeMetadata, catalog, recentCalibFinishSuccessKey, telemetry])

  useEffect(() => {
    setRecentCalibFinishSuccessKey(null)
  }, [robotId])

  const hasLocalDispatch = Boolean(localDispatch)
  const localBusy = hasLocalDispatch && !operationSettled
  const calibFinishUiComplete = recentCalibFinishSuccessKey != null
  // Lock other rows only when we know which catalog row holds mode 15 — otherwise after
  // reload we deadlock (global lock, no Finish button until confirmedKey resolves).
  const calibHeldOnRobot =
    activeMode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT &&
    !calibFinishUiComplete &&
    confirmedKeys.calibKey != null
  // Lock on mode 17 itself. A missing catalog row must not leave Wash clickable.
  const serviceHeldOnRobot = activeMode === ROBOT_MODES.MODE_SERVICE_POINT

  const isOperationInFlight = localBusy || calibHeldOnRobot || serviceHeldOnRobot
  const isGlobalLocked = isOperationInFlight

  const clearLocalDispatch = useCallback(
    (reason: string) => {
      if (!robotId) {
        return
      }
      writeCrossTabCatalogModeBusy(robotId, null)
      catalogCrossTabLog('clear-operation', { robotId, reason })
    },
    [robotId]
  )

  useEffect(() => {
    if (!robotId || !crossTabBusy || optimistic) {
      return
    }
    if (!shouldClearStaleCrossTabCatalogBusy(crossTabBusy, activeMode, serverPending ?? null)) {
      return
    }
    dispatch(RobotActions.clearServiceModeSwitchOptimistic())
    clearLocalDispatch('stale-cross-tab-reconcile')
  }, [activeMode, clearLocalDispatch, crossTabBusy, dispatch, optimistic, robotId, serverPending])

  useEffect(() => {
    if (!localDispatch || !operationSettled) {
      return
    }
    if (
      isCatalogCalibFinishAction(localDispatch.actionKey) &&
      activeMode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT &&
      calibFinishOnboardReadyKey
    ) {
      return
    }
    dispatch(RobotActions.clearServiceModeSwitchOptimistic())
    clearLocalDispatch('dispatch-settled')
  }, [
    activeMode,
    calibFinishOnboardReadyKey,
    calibrationRows,
    clearLocalDispatch,
    confirmedKeys.calibKey,
    confirmedKeys.serviceKey,
    dispatch,
    localDispatch,
    operationSettled,
    robotId,
  ])

  useEffect(() => {
    if (!robotId || !crossTabBusy || optimistic) {
      return
    }
    if (!operationSettled) {
      return
    }
    clearLocalDispatch('cross-tab-settled')
  }, [clearLocalDispatch, crossTabBusy, operationSettled, optimistic, robotId])

  useEffect(() => {
    if (!hasLocalDispatch) {
      return
    }
    if (operationSettled) {
      return
    }
    const operationTimeoutMs =
      localDispatch && isCatalogCalibFinishAction(localDispatch.actionKey)
        ? CALIB_FINISH_AWAIT_TIMEOUT_MS
        : CATALOG_OPERATION_TIMEOUT_MS
    const timer = window.setTimeout(() => {
      catalogCrossTabLog('operation-timeout', {
        robotId,
        kind: isCatalogCalibFinishAction(localDispatch?.actionKey ?? '') ? 'calib-finish-await' : 'catalog-operation',
        timeoutMs: operationTimeoutMs,
      })
      if (robotId && localDispatch?.actionKey) {
        setCatalogOperationTimeoutNotice(robotId, localDispatch.actionKey, activeMode ?? null)
      }
      dispatch(RobotActions.clearServiceModeSwitchOptimistic())
      clearLocalDispatch('timeout')
      if (robotId) {
        void store.dispatch(robotThunks.fetchRobotSilent(robotId))
      }
    }, operationTimeoutMs)
    return () => window.clearTimeout(timer)
  }, [
    clearLocalDispatch,
    dispatch,
    hasLocalDispatch,
    localDispatch,
    localDispatch?.actionKey,
    operationSettled,
    robotId,
    activeMode,
  ])

  useEffect(() => {
    const timedOut = readCatalogOperationTimeoutNotice()
    if (!timedOut || timedOut.robotId !== robotId) {
      return
    }
    if (timedOut.modeAtTimeout !== (activeMode ?? null)) {
      clearCatalogOperationTimeoutNotice()
    }
  }, [activeMode, robotId])

  useEffect(() => {
    if (!serverPending || operationSettled) {
      return
    }
    // Orphan API pending after reload — not while this tab tracks the same operation.
    if (localDispatch) {
      return
    }
    const fallbackMs = serverPending.timeoutSec * 1000 + MODE_SWITCH_TIMEOUT_PADDING_MS
    const timer = window.setTimeout(() => {
      catalogCrossTabLog('operation-timeout', { robotId, kind: 'pending-fallback', fallbackMs })
      dispatch(RobotActions.clearServiceModeSwitchOptimistic())
      clearLocalDispatch('pending-timeout')
      if (robotId) {
        void store.dispatch(robotThunks.fetchRobotSilent(robotId))
      }
    }, fallbackMs)
    return () => window.clearTimeout(timer)
  }, [clearLocalDispatch, dispatch, localDispatch, operationSettled, robotId, serverPending])

  const isActionLoading = (actionKey: string) => {
    if (localDispatch?.actionKey !== actionKey) {
      return false
    }
    return !operationSettled
  }

  return {
    isGlobalLocked,
    isOperationInFlight,
    isActionLoading,
    confirmedCalibKey: confirmedKeys.calibKey,
    confirmedServiceKey: confirmedKeys.serviceKey,
    recentCalibFinishSuccessKey,
    calibFinishOnboardReadyKey,
    serverPending,
    crossTabBusy,
  }
}
